import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from "prop-types";
import ProductReducer from 'Store/Product/Product.reducer';
import { withReducers } from 'Util/DynamicReducer';
import { HistoryType, LocationType, MatchType } from 'Type/Common';
import { ProductType } from 'Type/ProductList';
import { debounce } from 'Util/Request';
import { LOADING_TIME } from 'Route/CategoryPage/CategoryPage.config';
import { getIsConfigurableParameterSelected, getVariantIndex, getNewParameters } from 'Util/Product';

import {
    convertQueryStringToKeyValuePairs,
    removeQueryParamWithoutHistory,
    updateQueryParamWithoutHistory
} from 'Util/Url';

import  {
    ProductPageContainer as SourceProductPageContainer,
    mapStateToProps as SourcemapStateToProps,
    mapDispatchToProps as SourcemapDispatchToProps
}  from 'SourceRoute/ProductPage/ProductPage.container';

export const mapStateToProps = (state) => ({
    ...SourcemapStateToProps(state),
    brand: state.ProductReducer.product?.brandData ? state.ProductReducer.product.brandData : {},
    category: state.CategoryReducer?.category,
    popupType: state.PopupReducer.popupPayload
});

export const mapDispatchToProps = (dispatch) => ({
    ...SourcemapDispatchToProps(dispatch)
});

/** @namespace Route/ProductPage/Container */
export class ProductPageContainer extends SourceProductPageContainer {
    state = {
        configurableVariantIndex: -1,
        parameters: {},
        productOptionsData: {},
        selectedInitialBundlePrice: 0,
        selectedBundlePrice: 0,
        selectedBundlePriceExclTax: 0,
        selectedLinkPrice: 0,
        currentProductSKU: '',
        isUpSellActive : -1,
        pdpShareSectionTop : 0
    };

    containerFunctions = {
        updateConfigurableVariant: this.updateConfigurableVariant.bind(this),
        getLink: this.getLink.bind(this),
        getSelectedCustomizableOptions: this.getSelectedCustomizableOptions.bind(this),
        setBundlePrice: this.setBundlePrice.bind(this),
        setLinkedDownloadables: this.setLinkedDownloadables.bind(this),
        setLinkedDownloadablesPrice: this.setLinkedDownloadablesPrice.bind(this),
        isProductInformationTabEmpty: this.isProductInformationTabEmpty.bind(this),
        isProductAttributesTabEmpty: this.isProductAttributesTabEmpty.bind(this),
        isProductInstructionsTabEmpty: this.isProductInstructionsTabEmpty.bind(this),
        isProductComponentsTabEmpty: this.isProductComponentsTabEmpty.bind(this),
        selectPdpProductTab: this.selectPdpProductTab.bind(this),
        setPdpShareSectionTop: this.setPdpShareSectionTop.bind(this)
    };

    static propTypes = {
        location: LocationType,
        changeHeaderState: PropTypes.func.isRequired,
        setBigOfflineNotice: PropTypes.func.isRequired,
        changeNavigationState: PropTypes.func.isRequired,
        updateMetaFromProduct: PropTypes.func.isRequired,
        updateBreadcrumbs: PropTypes.func.isRequired,
        requestProduct: PropTypes.func.isRequired,
        isOffline: PropTypes.bool.isRequired,
        productSKU: PropTypes.string,
        productID: PropTypes.number,
        product: ProductType.isRequired,
        history: HistoryType.isRequired,
        match: MatchType.isRequired,
        goToPreviousNavigationState: PropTypes.func.isRequired,
        navigation: PropTypes.shape(PropTypes.shape).isRequired,
        metaTitle: PropTypes.string,
        addRecentlyViewedProduct: PropTypes.func.isRequired,
        store: PropTypes.string.isRequired,
        isMobile: PropTypes.bool.isRequired,
        brand: PropTypes.object.isRequired
    };

    static getDerivedStateFromProps(props, state) {
        const {
            product: {
                sku,
                variants,
                configurable_options,
                options,
                productOptionsData
            },
            popupType
            // location: { search }
        } = props;
        const { search } = location;
        const {
            currentProductSKU: prevSKU,
            productOptionsData: prevOptionData
        } = state;

        const currentProductSKU = prevSKU === sku ? '' : prevSKU;

        /**
         * If the product we expect to load is loaded -
         * reset expected SKU
         */
        if (!configurable_options && !variants) {
            return {
                currentProductSKU
            };
        }
        
        const parameters = Object.entries(convertQueryStringToKeyValuePairs(search))
            .reduce((acc, [key, value]) => {
                if (key in configurable_options) {
                    return { ...acc, [key]: value };
                }

                return acc;
            }, {});

        if (Object.keys(parameters).length !== Object.keys(configurable_options).length) {
            return {
                parameters,
                currentProductSKU
            };
        }

        const configurableVariantIndex = getVariantIndex(variants, parameters, true);

        const newOptionsData = options.reduce((acc, { option_id, required }) => {
            if (required) {
                acc.push(option_id);
            }

            return acc;
        }, []);

        const prevRequiredOptions = productOptionsData?.requiredOptions || [];
        const requiredOptions = [...prevRequiredOptions, ...newOptionsData];

        const newProps = {
            parameters,
            currentProductSKU,
            productOptionsData: {
                ...prevOptionData, ...productOptionsData, requiredOptions
            }
        }

        if(!popupType['MODIFACE']) { 
            newProps.configurableVariantIndex = configurableVariantIndex;
        }

        return newProps

    }

    componentDidMount() {
        
        /**
         * Always request product information. In this case we will have updated data.
         * Service worker will return previous information and updated new information
         * through broadcast.
         */
        this.requestProduct();

        /**
         * Always make sure the navigation switches into the MENU tab
         * */
        this.updateNavigationState();

        /**
         * Ensure transition PDP => homepage => PDP always having proper meta
         */
        this.updateMeta();

        /**
         * Make sure to update header state, the data-source will
         * define the correct information to use for update
         * (it can be a product, history state product or an empty object).
         */
        this.updateHeaderState();
        this.updateBreadcrumbs();

        /**
         * Scroll page top in order to display it from the start
         */
        this.scrollTop();

        this._addToRecentlyViewedProducts();
    }

    componentDidUpdate(prevProps) {
        const {
            isOffline,
            productSKU,
            product: {
                sku,
                options,
                items,
                attributes = {},
                id
            } = {}
        } = this.props;

        const {
            productSKU: prevProductSKU,
            product: {
                sku: prevSku,
                options: prevOptions,
                items: prevItems,
                id : prevId
            }
        } = prevProps;

        const { sku: stateSKU } = history?.state?.state?.product || {};

        if (isOffline) {
            debounce(this.setOfflineNoticeSize, LOADING_TIME)();
        }

        /**
         * We should also update product based data if, the URL
         * rewrite SKU has changed to matching the product history SKU
         * one. At this point there could be sufficient data for
         * some updates (i.e. header state).
         */
        if (
            productSKU !== prevProductSKU
            && stateSKU === productSKU
        ) {
            this.updateHeaderState();
        }

        /**
         * If the currently loaded category ID does not match the ID of
         * category ID from URL rewrite, request category.
         */
        if (productSKU !== sku) {
            this.requestProduct();
        }

        /**
         * If product ID was changed => it is loaded => we need to
         * update product specific information, i.e. breadcrumbs.
         */
        if (sku !== prevSku) {
            this.updateBreadcrumbs();
            this.updateHeaderState();
            this.updateMeta();
            this._addToRecentlyViewedProducts();
        }

        /**
         * LEGACY: needed to make sure required items are
         * selected in the bundle product.
         */
        if (JSON.stringify(options) !== JSON.stringify(prevOptions)) {
            this.getRequiredProductOptions(options);
        }

        /**
         * LEGACY needed to make sure required options are
         * selected in the customizable options product.
         */
        if (JSON.stringify(items) !== JSON.stringify(prevItems)) {
            this.getRequiredProductOptions(items);
        }

        // this._addToRecentlyViewedProducts();
    }

    containerProps = () => {
        const { isMobile, brand, category } = this.props;
        const {
            configurableVariantIndex,
            parameters,
            productOptionsData,
            selectedBundlePrice,
            selectedBundlePriceExclTax,
            selectedInitialBundlePrice,
            selectedLinkPrice,
            isUpSellActive,
            pdpShareSectionTop
        } = this.state;

        return {
            areDetailsLoaded: this.getAreDetailsLoaded(),
            configurableVariantIndex,
            dataSource: this.getDataSource(),
            isAttributesTabEmpty: this.isProductAttributesTabEmpty(),
            isComponentsTabEmpty: this.isProductComponentsTabEmpty(),
            isCompositionTabEmpty: this.isProductCompositionTabEmpty(),
            isInstructionsTabEmpty: this.isProductInstructionsTabEmpty(),
            isInformationTabEmpty: this.isProductInformationTabEmpty(),
            isMobile,
            brand,
            category,
            parameters,
            productOptionsData,
            productOrVariant: this.getProductOrVariant(),
            selectedBundlePrice,
            selectedBundlePriceExclTax,
            selectedInitialBundlePrice,
            selectedLinkPrice,
            isUpSellActive,
            pdpShareSectionTop
        };
    };

    updateUrl(key, value, parameters) {
        const { location: originalLocation, history } = this.props;

        const isParameterSelected = getIsConfigurableParameterSelected(parameters, key, value);

        if (isParameterSelected) {
            //https://mageguide.atlassian.net/browse/HONDOSC-877
            updateQueryParamWithoutHistory(
                    key, 
                    value, 
                    history, 
                    originalLocation?.search ? originalLocation : location
                );
        } else {
            removeQueryParamWithoutHistory(key, history, originalLocation);
        }
    }

    updateConfigurableVariant(key, value) {
        const { parameters: prevParameters } = this.state;
        const { popupType } = this.props;

        const parameters = getNewParameters(prevParameters, key, value);
        this.setState({ parameters });

        if(!popupType['MODIFACE']) {
            this.updateUrl(key, value, parameters);
        }

        this.updateConfigurableVariantIndex(parameters);
    }

    requestProduct() {
        const { requestProduct, productSKU } = this.props;
        const { currentProductSKU } = this.state;

        /**
         * If URL rewrite was not passed - do not request the product.
         */
        if (!productSKU) {
            return;
        }

        /**
         * Skip loading the same product SKU the second time
         */
        if (currentProductSKU === productSKU) {
            return;
        }

        this.setState({ currentProductSKU: productSKU });

        const options = {
            isSingleProduct: true,
            args: {
                // search:productSKU,
                filter: this.getProductRequestFilter()
            }
        };

        requestProduct(options);
    }

    setPdpShareSectionTop (itemValue) {
        if(this.state.pdpShareSectionTop !== itemValue){
            this.setState({
                pdpShareSectionTop : itemValue
            });
        }
    }

    selectPdpProductTab(isUpSellActive) {
        this.setState({ isUpSellActive });
    }

    isProductComponentsTabEmpty() {
        const dataSource = this.getDataSource();

        return dataSource?.attributes?.components?.attribute_value ? false : true;
    }

    isProductCompositionTabEmpty() {
        const dataSource = this.getDataSource();

        return dataSource?.attributes?.composition?.attribute_value ? false : true;
    }

    isProductInstructionsTabEmpty() {
        const dataSource = this.getDataSource();

        return dataSource?.attributes?.instructions?.attribute_value ? false : true;
    }
}

export default withReducers({ ProductReducer })(withRouter(
    connect(mapStateToProps, mapDispatchToProps)(ProductPageContainer)
));
